import { useState } from 'react';
import { useGVVRVoiceOrchestration } from '../../hooks/useGVVRVoiceOrchestration';
import type { VoiceFlowPhase } from '../../types/orchestration';


const PhaseLabels: Record<VoiceFlowPhase, string> = {
  ready: 'Ready to Start',
  giver: 'Understanding You',
  recipient: 'Learning About Recipient', 
  context: 'Exploring the Occasion',
  recommendations: 'Generating Recommendations',
  complete: 'Complete',
  error: 'Error Occurred',
  fallback: 'Fallback Mode'
};

const PhaseDescriptions: Record<VoiceFlowPhase, string> = {
  ready: 'Click "Start Voice Curation" to begin your personalized gift discovery journey.',
  giver: 'Grace is analyzing your gift-giving personality and preferences through conversation.',
  recipient: 'Now we\'re learning about the person you\'re shopping for.',
  context: 'Understanding the context and significance of this gift-giving occasion.',
  recommendations: 'Generating personalized gift recommendations based on all the insights.',
  complete: 'Your personalized gift recommendations are ready!',
  error: 'Something went wrong. You can try again or switch to text mode.',
  fallback: 'Switched to text-based interface for better accessibility.'
};

export function GVVRVoiceOrchestrationDemo() {
  const [userName, setUserName] = useState('');
  const [recipientName, setRecipientName] = useState('');

  const orchestration = useGVVRVoiceOrchestration({
    onPhaseChange: (phase) => {
      console.log(`🎯 Phase changed to: ${phase}`);
    },
    onFlowComplete: (recommendations) => {
      console.log('🎉 Flow completed with recommendations:', recommendations);
    },
    onError: (error) => {
      console.error('❌ Orchestration error:', error);
    }
  });

  const handleStartFlow = async () => {
    if (!userName.trim()) {
      alert('Please enter your name first');
      return;
    }
    if (!recipientName.trim()) {
      alert('Please enter the recipient\'s name');
      return;
    }

    try {
      await orchestration.startFlow(userName, recipientName);
    } catch (error) {
      console.error('Failed to start flow:', error);
    }
  };

  const renderPhaseProgress = () => {
    const phases: VoiceFlowPhase[] = ['ready', 'giver', 'recipient', 'context', 'recommendations', 'complete'];
    const currentIndex = phases.indexOf(orchestration.currentPhase);
    
    return (
      <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
        <div 
          className="bg-purple-600 h-2 rounded-full transition-all duration-500"
          style={{ width: `${Math.max(0, (currentIndex / (phases.length - 1)) * 100)}%` }}
        />
      </div>
    );
  };

  const renderFlowData = () => {
    if (Object.keys(orchestration.flowData).length === 0) return null;

    return (
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="font-semibold mb-3">Session Progress</h3>
        
        {/* Giver Analysis - Match GVVRComplete.tsx style */}
        {orchestration.flowData.giverAnalysis && (
          <div className="mb-6 bg-white rounded-3xl shadow-xl p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Your Gift-Giving Archetype: {orchestration.flowData.giverAnalysis.primaryArchetype}
              </h2>
              <p className="text-gray-600 text-lg">{orchestration.flowData.giverAnalysis.confidence}% confidence</p>
            </div>
            {orchestration.flowData.giverAnalysis.personalizedInsights && 
             orchestration.flowData.giverAnalysis.personalizedInsights.length > 0 && (
              <div className="grid md:grid-cols-3 gap-4">
                {orchestration.flowData.giverAnalysis.personalizedInsights.map((insight: string, index: number) => (
                  <div key={index} className="p-4 bg-indigo-50 rounded-xl">
                    <p className="text-indigo-700 text-sm">{insight}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Recipient Analysis - Match GVVRComplete.tsx style */}
        {orchestration.flowData.recipientAnalysis && (
          <div className="mb-6 bg-white rounded-3xl shadow-xl p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                {recipientName}'s Archetype: {orchestration.flowData.recipientAnalysis.primaryArchetype}
              </h2>
              <p className="text-gray-600 text-lg">{orchestration.flowData.recipientAnalysis.confidence}% confidence</p>
            </div>
            {orchestration.flowData.recipientAnalysis.personalizedInsights && 
             orchestration.flowData.recipientAnalysis.personalizedInsights.length > 0 && (
              <div className="grid md:grid-cols-3 gap-4">
                {orchestration.flowData.recipientAnalysis.personalizedInsights.map((insight: string, index: number) => (
                  <div key={index} className="p-4 bg-purple-50 rounded-xl">
                    <p className="text-purple-700 text-sm">{insight}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Occasion Context */}
        {orchestration.flowData.occasion && (
          <div className="mb-3 bg-white p-3 rounded border-l-4 border-green-500">
            <span className="text-sm font-medium text-green-600">Occasion:</span>
            <div className="text-sm font-semibold text-gray-800 mt-1">
              {orchestration.flowData.occasion.occasion_type}
            </div>
            {orchestration.flowData.occasion.emotion_goals && (
              <div className="text-sm text-gray-600 mt-1">
                Goals: {orchestration.flowData.occasion.emotion_goals.join(', ')}
              </div>
            )}
            {orchestration.flowData.occasion.timeline && (
              <div className="text-xs text-gray-500 mt-1">
                Timeline: {orchestration.flowData.occasion.timeline}
              </div>
            )}
          </div>
        )}

        {/* Show relationship dynamics if both analyses are complete */}
        {orchestration.flowData.giverAnalysis && orchestration.flowData.recipientAnalysis && (
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-3 rounded border border-purple-200">
            <div className="text-sm font-medium text-purple-700 mb-1">
              ✨ Relationship Dynamic
            </div>
            <div className="text-sm text-gray-700">
              {orchestration.flowData.giverAnalysis.primaryArchetype} → {orchestration.flowData.recipientAnalysis.primaryArchetype}
            </div>
            <div className="text-xs text-gray-600 mt-1">
              This pairing suggests thoughtful, personalized gift opportunities
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderRecommendations = () => {
    if (!orchestration.recommendations) return null;

    const recs = orchestration.recommendations as {
      recommendations: Array<{
        title: string;
        description: string;
        reasoning: string;
        price_range: string;
        confidence: number;
        category: string;
      }>;
      summary: string;
      confidence: number;
    };

    return (
      <div className="bg-white border border-green-200 p-6 rounded-lg">
        <h3 className="text-xl font-semibold text-green-800 mb-4">
          🎁 Your Personalized Recommendations
        </h3>
        
        <p className="text-gray-600 mb-4">{recs.summary}</p>
        
        <div className="space-y-4">
          {recs.recommendations.map((rec, index) => (
            <div key={index} className="border border-gray-200 p-4 rounded-lg">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-semibold">{rec.title}</h4>
                <span className="text-sm text-gray-500">{rec.price_range}</span>
              </div>
              <p className="text-gray-600 mb-2">{rec.description}</p>
              <p className="text-sm text-gray-500 italic">{rec.reasoning}</p>
              <div className="flex justify-between items-center mt-2">
                <span className="text-xs bg-gray-100 px-2 py-1 rounded">{rec.category}</span>
                <span className="text-xs text-green-600">{rec.confidence}% match</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          🎁 GVVR Voice Orchestration Demo
        </h1>
        <p className="text-gray-600">
          Experience our multi-agent voice curation system
        </p>
      </div>

      {/* Progress Bar */}
      {renderPhaseProgress()}

      {/* Current Phase Status */}
      <div className="bg-white border border-gray-200 p-6 rounded-lg mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">
            {PhaseLabels[orchestration.currentPhase]}
          </h2>
          <div className="flex items-center space-x-2">
            {orchestration.isProcessing && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600" />
            )}
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              orchestration.isReady ? 'bg-blue-100 text-blue-800' :
              orchestration.isActive ? 'bg-yellow-100 text-yellow-800' :
              orchestration.isComplete ? 'bg-green-100 text-green-800' :
              orchestration.hasError ? 'bg-red-100 text-red-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {orchestration.currentPhase}
            </span>
          </div>
        </div>
        
        <p className="text-gray-600 mb-4">
          {PhaseDescriptions[orchestration.currentPhase]}
        </p>

        {orchestration.isProcessing && (
          <p className="text-purple-600 font-medium">
            {orchestration.processingMessage}
          </p>
        )}

        {orchestration.error && (
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <p className="text-red-800 font-medium">Error:</p>
            <p className="text-red-600">{orchestration.error.message}</p>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="bg-gray-50 p-6 rounded-lg mb-6">
        {orchestration.isReady && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Name
              </label>
              <input
                type="text"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder="Enter your name"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recipient's Name
              </label>
              <input
                type="text"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                placeholder="Who are you shopping for?"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <button
              onClick={handleStartFlow}
              disabled={!userName.trim() || !recipientName.trim()}
              className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              🎤 Start Voice Curation
            </button>
          </div>
        )}

        {orchestration.isActive && (
          <button
            onClick={orchestration.stopFlow}
            className="w-full bg-red-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-red-700 transition-colors"
          >
            🛑 Stop Session
          </button>
        )}

        {(orchestration.isComplete || orchestration.hasError) && (
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            🔄 Start New Session
          </button>
        )}
      </div>

      {/* Flow Data */}
      {renderFlowData()}

      {/* Recommendations */}
      {orchestration.isComplete && renderRecommendations()}

      {/* Analytics Panel Removed - Using simplified demo */}
    </div>
  );
} 